import * as React from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    MenuItem,
    Pagination,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { getMatchExternalBets, resyncMatchExternalBet, updateMatchExternalBetStatus } from '../../Api';
import ExternalPlacedBetDto, { ExternalPlacedBetResultState } from '../../models/dtos/ExternalPlacedBetDto';
import MatchVarietyDto from '../../models/dtos/MatchVarietyDto';

const resultStates: ExternalPlacedBetResultState[] = ['PENDING', 'WON', 'LOST', 'RETURNED', 'CANCELLED'];

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function resultColor(state: ExternalPlacedBetResultState): 'default' | 'success' | 'error' | 'warning' | 'info' {
    if (state === 'WON') return 'success';
    if (state === 'LOST' || state === 'CANCELLED') return 'error';
    if (state === 'PENDING') return 'warning';
    return 'info';
}

export default function MatchExternalBetsTab(props: { matchId: number; varieties: MatchVarietyDto[] }) {
    const pageSize = 25;
    const [items, setItems] = React.useState<ExternalPlacedBetDto[]>([]);
    const [page, setPage] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [updatingId, setUpdatingId] = React.useState<number | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [message, setMessage] = React.useState<string | null>(null);

    const load = React.useCallback(async (nextPage: number) => {
        setLoading(true);
        setError(null);
        const response = await getMatchExternalBets(props.matchId, (nextPage - 1) * pageSize, pageSize);
        if (response == null) {
            setError('External bets could not be loaded.');
        } else {
            setItems(response.items ?? []);
            setTotalItems(response.totalItems ?? 0);
            setPage(nextPage);
        }
        setLoading(false);
    }, [props.matchId]);

    React.useEffect(() => {
        load(1);
    }, [load]);

    const updateStatus = async (item: ExternalPlacedBetDto, event: SelectChangeEvent) => {
        const resultState = event.target.value as ExternalPlacedBetResultState;
        setUpdatingId(item.externalReferenceId);
        setError(null);
        setMessage(null);
        const result = await updateMatchExternalBetStatus(
            props.matchId,
            item.externalReferenceId,
            resultState,
        );
        if (!result.ok || result.data == null) {
            setError(result.error ?? 'External bet status was not updated.');
        } else {
            setItems(current => current.map(row => row.externalReferenceId === item.externalReferenceId ? result.data! : row));
            setMessage(`External bet ${item.placedBetId} updated and published.`);
        }
        setUpdatingId(null);
    };

    const resync = async (item: ExternalPlacedBetDto) => {
        setUpdatingId(item.externalReferenceId);
        setError(null);
        setMessage(null);
        const result = await resyncMatchExternalBet(props.matchId, item.externalReferenceId);
        if (!result.ok || result.data == null) {
            setError(result.error ?? 'External bet was not re-synced.');
        } else {
            setItems(current => current.map(row => row.externalReferenceId === item.externalReferenceId ? result.data! : row));
            setMessage(`External bet ${item.placedBetId} re-synced with status ${item.resultState}.`);
        }
        setUpdatingId(null);
    };

    const varietyName = (varietyId?: number | null) => {
        if (varietyId == null) return '-';
        return props.varieties.find(variety => variety.id === varietyId)?.name ?? `Variety ${varietyId}`;
    };
    const totalPages = Math.ceil(totalItems / pageSize);

    return (
        <Stack gap={2} sx={{ py: 2 }}>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : items.length === 0 ? (
                <Typography color="text.secondary">No external bets were placed for this match.</Typography>
            ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 1250 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Placed bet</TableCell>
                                <TableCell>External source</TableCell>
                                <TableCell>External reference</TableCell>
                                <TableCell>Placed at</TableCell>
                                <TableCell>Bet</TableCell>
                                <TableCell>Variety</TableCell>
                                <TableCell>Coefficient</TableCell>
                                <TableCell>Delivery</TableCell>
                                <TableCell>Result</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(item => (
                                <TableRow key={item.externalReferenceId} hover>
                                    <TableCell>{item.placedBetId}</TableCell>
                                    <TableCell><Chip size="small" label={item.externalSource} variant="outlined" /></TableCell>
                                    <TableCell sx={{ maxWidth: 260, overflowWrap: 'anywhere' }}>{item.externalId}</TableCell>
                                    <TableCell>{formatDate(item.placedAt)}</TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {item.betGroupName ?? `Bet group ${item.betTypeGroupId ?? '-'}`}
                                        </Typography>
                                        {item.betName ?? `Bet type ${item.betTypeId ?? '-'}`} ({item.betTypeParam})
                                    </TableCell>
                                    <TableCell>{varietyName(item.sportMatchVarietyId)}</TableCell>
                                    <TableCell>{item.placedCoefficient}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.status}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Sent: {formatDate(item.lastSentAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Chip size="small" label={item.resultState} color={resultColor(item.resultState)} />
                                            <FormControl size="small" sx={{ minWidth: 125 }} disabled={updatingId === item.externalReferenceId}>
                                                <Select value={item.resultState} onChange={event => updateStatus(item, event)}>
                                                    {resultStates.map(state => <MenuItem key={state} value={state}>{state}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                            <Tooltip title="Re-sync current status">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => resync(item)}
                                                        disabled={updatingId === item.externalReferenceId}
                                                        aria-label="Re-sync current external bet status"
                                                    >
                                                        <SyncIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {updatingId === item.externalReferenceId && <CircularProgress size={18} />}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {totalPages > 1 && (
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_event, nextPage) => load(nextPage)}
                    showFirstButton
                    showLastButton
                />
            )}
        </Stack>
    );
}
